import { ReactElement } from 'react';
import { LAMBDA_DEFAULT_PAGE_SIZE } from '@localstack/constants';
import { getAWSRowCount, getSchema } from '@localstack/services';
import { Box, Chip, Link, Tooltip } from '@mui/material';
import { LambdaWebEndpointSummary, LambdaWebRevisionWeight } from '@localstack/types';

import { MagicTable } from '../../magic/MagicTable';

export type LambdaWebEndpointsTableProps = {
  page?: number;
  onPageChange?: (page: number) => void;
  hasMore?: boolean;
  loading?: boolean;
  endpoints: LambdaWebEndpointSummary[];
  onSelect?: (names: string[]) => void;
  onViewEndpoint?: (name: string) => void;
};

type RevisionWeightsProps = {
  weights: LambdaWebRevisionWeight[];
};

const RevisionWeights = ({ weights }: RevisionWeightsProps): ReactElement => {
  const total = weights.reduce((sum, { weight }) => sum + (weight ?? 0), 0);
  return (
    <Box display="flex" gap={0.5} flexWrap="wrap">
      {weights.map(({ revisionId, weight }) => (
        <Tooltip key={revisionId} title={`Revision ${revisionId} receives ${weight} of ${total}`}>
          <Chip size="small" label={`${revisionId}: ${total > 0 ? Math.round(((weight ?? 0) / total) * 100) : 0}%`} />
        </Tooltip>
      ))}
    </Box>
  );
};

export const LambdaWebEndpointsTable = ({
  page = 0,
  onPageChange,
  hasMore = false,
  loading,
  endpoints,
  onSelect,
  onViewEndpoint,
}: LambdaWebEndpointsTableProps): ReactElement => (
  <MagicTable
    schema={getSchema('LambdaWeb')}
    entry="FunctionEndpointSummary"
    idAttribute="endpointName"
    order={['endpointName', 'endpointType', 'authType', 'domainName', 'revisionWeights', 'state']}
    rows={endpoints}
    page={page}
    onPageChange={onPageChange}
    paginationMode="server"
    pageSize={LAMBDA_DEFAULT_PAGE_SIZE}
    rowsPerPageOptions={[LAMBDA_DEFAULT_PAGE_SIZE]}
    rowCount={getAWSRowCount({ page, DEFAULT_PAGE_SIZE: LAMBDA_DEFAULT_PAGE_SIZE, hasMore, array: endpoints })}
    selectable={!!onSelect}
    onSelect={onSelect}
    loading={loading}
    externalFields={{
      endpointName: (row: LambdaWebEndpointSummary) => (
        <Link onClick={() => row.endpointName && onViewEndpoint?.(row.endpointName)} underline="hover">
          {row.endpointName}
        </Link>
      ),
      domainName: (row: LambdaWebEndpointSummary) =>
        row.domainName ? (
          <Link href={`http://${row.domainName}`} target="_blank" rel="noopener noreferrer" underline="hover">
            {row.domainName}
          </Link>
        ) : (
          <></>
        ),
      revisionWeights: (row: LambdaWebEndpointSummary) => <RevisionWeights weights={row.revisionWeights ?? []} />,
    }}
  />
);
